/**
 * @fileoverview Dialog component for entering Click&Pledge Givent tokens.
 * Provides a modal dialog for users to input their Givent authentication token.
 */


import { FieldTextStateless as TextField } from '@atlaskit/field-text';
import React, { Component } from 'react';

import { Dialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n';
import { Icon, IconArrowUpRight } from '../../../base/icons';
import { getLocalParticipant } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { bindStreamOptions, isClickandPledge, setGiventToken, setUserId } from '../../actions';

const DEFAULT_STREAM_OPTIONS = {
    SPM: 1,
    SPS: 1,
    SVS: 1
};

/**
 * Normalizes stream options to numeric flags.
 *
 * @param {Object} options - Raw stream options.
 * @returns {Object}
 */
function normalizeStreamOptions(options = {}) {
    return {
        SPM: options.SPM === 0 ? 0 : 1,
        SPS: options.SPS === 0 ? 0 : 1,
        SVS: options.SVS === 0 ? 0 : 1
    };
}

/**
 * Implements a React Component for displaying a dialog with a field
 * for setting the local participant's Givent token for Click&Pledge donations.
 *
 * @augments Component
 */
class clickPledgeDialog extends Component {
    /**
     * Initializes a new clickPledgeDialog instance.
     * Sets up initial state and binds event handlers.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized
     */
    constructor(props) {
        super(props);

        this.state = {
            giventToken: '',
            streamcheckboxes: DEFAULT_STREAM_OPTIONS
        };

        this._onGiventTokenChnage = this._onGiventTokenChnage.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._onCheckboxChange = this._onCheckboxChange.bind(this);
        this._onRedirectToGivent = this._onRedirectToGivent.bind(this);
    }

    /**
     * Handles checkbox change events for stream options.
     * Updates the streamcheckboxes state based on checkbox interactions.
     *
     * @param {Object} event - The DOM event triggered from checkbox change
     * @private
     * @returns {void}
     */
    _onCheckboxChange(event) {
        const { name, checked } = event.target;

        this.setState(prevState => {
            return {
                ...prevState,
                streamcheckboxes: {
                    ...prevState.streamcheckboxes,
                    [name]: checked ? 1 : 0
                }
            };
        });
    }

    /**
     * Redirects to Givent help documentation in a new tab.
     * Prevents default behavior and opens the link without affecting current page.
     *
     * @param {Object} event - The DOM event triggered from the click
     * @private
     * @returns {void}
     */
    _onRedirectToGivent(event) {
        event.preventDefault();
        event.stopPropagation();
        window.open(
            'https://support.clickandpledge.com/hc/en-us/articles/36977786625435-CONNECT-Givent',
            '_blank',
            'noopener,noreferrer'
        );
    }


    /**
     * Implements React's render method.
     * Renders a dialog with a text field for entering the Givent token.
     *
     * @inheritdoc
     * @returns {ReactElement} The rendered dialog component
     */
    render() {
        return (
            <Dialog
                cancelDisabled = { false }
                disableCloseIcon = { false }
                disableOverlay = { false }
                isModal = { false }
                okKey = { this.props.t('clickandpledge.connect') }
                onSubmit = { this._onSubmit }
                titleKey = 'clickandpledge.title'
                width = 'small'>
                <TextField
                    autoFocus = { true }
                    compact = { true }
                    label = { this.props.t('clickandpledge.token') }
                    name = 'GiventToken'
                    onChange = { this._onGiventTokenChnage }
                    placeholder = { this.props.t('clickandpledge.placeHolder') }
                    shouldFitContainer = { true }
                    type = 'text'
                    value = { this.state.giventToken } />
                <div className = 'clickandpledge_dialog_checkbox'>
                    <div className = 'form_input_wrapper'>
                        <input
                            checked = { this.state.streamcheckboxes.SPM === 1 }
                            name = 'SPM'
                            onChange = { this._onCheckboxChange }
                            type = 'checkbox' />
                        <label>{this.props.t('clickandpledge.showProgressMeter')}</label>
                    </div>
                    <div className = 'form_input_wrapper'>
                        <input
                            checked = { this.state.streamcheckboxes.SPS === 1 }
                            name = 'SPS'
                            onChange = { this._onCheckboxChange }
                            type = 'checkbox' />
                        <label>{this.props.t('clickandpledge.showPaymentStream')}</label>
                    </div>
                    <div className = 'form_input_wrapper'>
                        <input
                            checked = { this.state.streamcheckboxes.SVS === 1 }
                            name = 'SVS'
                            onChange = { this._onCheckboxChange }
                            type = 'checkbox' />
                        <label>{this.props.t('clickandpledge.showScreenMarquee')}</label>
                    </div>
                </div>
                <div
                    className = 'clickandpledge-dialog'
                    onClick = { this._onRedirectToGivent }><span>{this.props.t('clickandpledge.helpTip')}</span><Icon src = { IconArrowUpRight } /></div>
            </Dialog>);
    }

    /**
     * Updates the entered Givent Token in component state.
     *
     * @param {Object} event - The DOM event triggered from the text field change
     * @private
     * @returns {void}
     */
    _onGiventTokenChnage(event) {
        this.setState({
            giventToken: event.target.value
        });
    }


    /**
     * Dispatches an action to update the local participant's Givent Token.
     * A token must be entered for the action to dispatch successfully.
     * Also dispatches stream options based on checkbox selections.
     *
     * @private
     * @returns {boolean} True if token was set successfully, false otherwise
     */
    _onSubmit() {
        const { giventToken, streamcheckboxes } = this.state;
        const { localParticipantId, conference } = this.props;

        if (giventToken) {
            this.props._onSetToken(giventToken, localParticipantId, conference, streamcheckboxes);

            return true;
        }

        return false;
    }
}

/**
 * Maps Redux state to component props.
 * Extracts local participant ID and conference instance.
 *
 * @param {Object} state - The Redux state
 * @returns {Object} Object containing mapped state properties
 */
export function _mapStateToProps(state) {
    const localParticipantId = getLocalParticipant(state)?.id;
    const conference = APP.store.getState()['features/base/conference'].conference;

    return {
        localParticipantId,
        conference
    };
}


/**
 * Maps dispatching of actions to React component props.
 * Provides functions for setting Givent token and enabling Click&Pledge.
 *
 * @param {Function} dispatch - Redux action dispatcher
 * @private
 * @returns {Object} Object containing dispatch functions
 */
function _mapDispatchToProps(dispatch) {
    return {
        _onSetToken(val, id, conference, streamOptions) {
            dispatch(setGiventToken(val));
            dispatch(setUserId(id));
            if (val !== undefined && val !== null) {
                dispatch(isClickandPledge(true));
            }

            // Dispatch stream options if provided
            if (streamOptions) {
                dispatch(bindStreamOptions(normalizeStreamOptions(streamOptions)));
            }
        }
    };

}

export default translate(connect(_mapStateToProps, _mapDispatchToProps)(clickPledgeDialog));
